"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, Loader, ArrowRight, Terminal } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
function AnalysisForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in duration-500">
        <div className="w-16 h-16 rounded-md bg-primary/10 flex items-center justify-center mb-4">
          <Terminal className="w-8 h-8 text-primary" />
        </div>
        <h3 className="font-default text-2xl font-bold text-foreground">Request Received</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary/20 hover:bg-primary/5">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Full Name</FormLabel>
              <FormControl>
                <Input {...field} className="bg-transparent border-0 border-b border-border/50 focus-visible:ring-0 focus-visible:border-primary transition-colors text-white" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" {...field} className="bg-transparent border-0 border-b border-border/50 focus-visible:ring-0 focus-visible:border-primary transition-colors text-white" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" {...field} className="bg-transparent border-0 border-b border-border/50 focus-visible:ring-0 focus-visible:border-primary transition-colors text-white" />
              </FormControl>
              <FormDescription className="font-mono text-[10px] text-muted-foreground/60">Direct line for faster response.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" rules={{
        required: "Property type is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Property Type</FormLabel>
              <FormControl>
                <Input {...field} className="bg-transparent border-0 border-b border-border/50 focus-visible:ring-0 focus-visible:border-primary transition-colors text-white" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="project_type" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Service Required</FormLabel>
              <FormControl>
                <div className="relative">
                  <select {...field} className="flex h-9 w-full appearance-none border-0 border-b border-border/50 bg-transparent px-0 py-1 text-base transition-colors outline-none focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-white">
                    {["Interior & Exterior", "Exterior Only", "Commercial Low-Rise", "Hard Water Removal", "Seasonal Maintenance"].map((opt, i) => <option key={i} value={opt} className="bg-card text-foreground">
                        {opt}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                    <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="budget" rules={{
        min: {
          value: 0,
          message: "Budget cannot be negative"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Estimated Budget</FormLabel>
              <FormControl>
                <Input type="number" {...field} className="bg-transparent border-0 border-b border-border/50 focus-visible:ring-0 focus-visible:border-primary transition-colors text-white" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="description" rules={{
      required: "Details are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Service Details</FormLabel>
            <FormControl>
              <Textarea className="min-h-[120px] bg-transparent border-0 border-b border-border/50 focus-visible:ring-0 focus-visible:border-primary resize-none transition-colors text-white" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-mono flex items-center gap-2">
            <span className="w-2 h-2 bg-destructive rounded-lg animate-pulse"></span>
            Error: {submitError}
          </p>
        </div>}

      <div className="space-y-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-base font-default font-bold tracking-widest uppercase bg-primary text-primary-foreground hover:opacity-90 transition-all shadow-lg shadow-primary/10 animate-pulse">
          <span className="relative flex items-center justify-center gap-2">
            {isSubmitting ? <>
                <Loader className="animate-spin size-5" />
                Calculating...
              </> : <>
                Get My Quote
                <ArrowRight className="size-5" />
              </>}
          </span>
        </Button>
        <p className="text-center text-muted-foreground text-xs font-mono uppercase tracking-tighter">
          6-hour response time guaranteed
        </p>
      </div>
    </Form>;
}
export default function SecureYourFreeQuote() {
  return <section data-section-id="224949" id="secure-your-free-quote" className="bg-background text-foreground relative">
      <div className="bg-background text-foreground py-24 lg:py-32 relative overflow-hidden">
        
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-white/5 via-background to-background pointer-events-none"></div>
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border/20 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border/20 to-transparent"></div>
        
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none"></div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            <div className="lg:col-span-5 flex flex-col space-y-10">
              <div>
                
                <h2 className="font-default text-5xl md:text-7xl font-bold text-foreground mb-6 tracking-tighter leading-[0.9]"><span className="text-primary">Ready for the </span><br /><span className="text-muted-foreground" contenteditable="false" style={{}}>6SPC Shine?</span></h2>
                <p className="text-muted-foreground text-xl max-w-md font-default leading-relaxed">
                  Experience the clarity of professional window cleaning. Serving the GTA, Toronto, and north of Newmarket with 5 years of elite expertise.
                </p>
              </div>

              <div className="border border-white/10 bg-secondary/30 backdrop-blur-2xl rounded-md p-10 relative overflow-hidden group shadow-2xl">
                <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-50"></div>
                
                <h3 className="font-default text-sm uppercase tracking-[0.3em] mb-10 text-muted-foreground">
                  Direct Contact
                </h3>

                <div className="space-y-8 font-default">
                  <div className="flex items-start gap-5 group/item">
                    <div className="p-3 rounded-md bg-white/5 text-foreground border border-white/10 group-hover/item:bg-white group-hover/item:text-black transition-all duration-500">
                      <MapPin className="lucide lucide-map-pin lucide lucide-map-pin w-5 h-5 !bg-transparent" />
                    </div>
                    <div>
                      <div className="text-muted-foreground text-[10px] uppercase tracking-widest mb-1 font-mono">Service Area</div>
                      <div className="font-medium text-primary">Toronto & GTA</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-5 group/item">
                    <div className="p-3 rounded-md bg-white/5 text-foreground border border-white/10 group-hover/item:bg-white group-hover/item:text-black transition-all duration-500">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-muted-foreground text-[10px] uppercase tracking-widest mb-1 font-mono">Call Leon</div>
                      <div className="text-foreground font-medium"><span className="text-primary">289-395-1652</span></div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-5 group/item">
                    <div className="p-3 rounded-md bg-white/5 text-foreground border border-white/10 group-hover/item:bg-white group-hover/item:text-black transition-all duration-500">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-muted-foreground text-[10px] uppercase tracking-widest mb-1 font-mono">Email Inquiry</div>
                      <div className="text-foreground font-medium"><span className="text-primary">info@6starprocleaners.com</span></div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-5 group/item">
                    <div className="p-3 rounded-md bg-white/5 text-foreground border border-white/10 group-hover/item:bg-white group-hover/item:text-black transition-all duration-500">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-muted-foreground text-[10px] uppercase tracking-widest mb-1 font-mono">Availability</div>
                      <div className="text-foreground font-medium"><span className="text-primary">7 Days a Week: 8am - 8pm</span></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="bg-card border border-white/5 backdrop-blur-3xl rounded-md p-8 md:p-12 shadow-4xl relative">
                <div className="absolute top-0 left-0 w-8 h-8 border-t border-l border-white/20 rounded-tl-md"></div>
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b border-r border-white/20 rounded-br-md"></div>

                <div className="mb-12 border-b border-white/5 pb-8">
                  <h3 className="font-default text-3xl font-bold text-foreground tracking-tight"><span className="text-primary">Request Your Quote</span></h3>
                  <p className="text-muted-foreground mt-3 text-base font-default">Provide your property details for a tailored luxury cleaning estimate.</p>
                </div>

                <FormProvider formKey="free_quote_form_home" formId="free_quote_form_home" sectionName="Start Your Project Analysis" defaultValues={{
                name: "",
                email: "",
                phone: "",
                company: "",
                project_type: "",
                budget: "",
                description: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true
                },
                company: {
                  required: true
                },
                project_type: {
                  required: true
                },
                budget: {
                  min: 0
                },
                description: {
                  required: true
                }
              }} submitText="Get My Quote" successMessage="Your quote request has been received. Leon will contact you within 6 hours.">
                  <AnalysisForm />
                </FormProvider>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>;
}